import React from 'react';
import { NavItem } from '../types';
import Logo from '../assets/favicon.jpeg';
import { LayoutDashboard, Store, Package, Receipt, BarChart2, Users, Settings, ScrollText } from 'lucide-react';

type UserRole = 'admin' | 'manager' | 'staff' | 'customer' | 'dev';

const roleAccess: Record<UserRole, string[]> = {
  admin: ['dashboard', 'stores', 'products', 'promotions', 'transactions', 'analytics', 'reports', 'users', 'logs', 'settings'],
  manager: ['dashboard', 'stores', 'products', 'promotions', 'transactions', 'analytics'],
  staff: ['dashboard', 'products', 'transactions'],
  customer: [],
  dev: ['users', 'logs'],
};

interface SidebarProps {
  currentPage: string;
  setCurrentPage: (page: string) => void;
  totalUnread?: number;
}

const Sidebar: React.FC<SidebarProps> = ({ currentPage, setCurrentPage, totalUnread }) => {
  const user = JSON.parse(localStorage.getItem('authUser') || '{}');
  const role: UserRole = (user?.role as UserRole) || 'manager';
  const allowed = roleAccess[role] || [];

  const navItems: NavItem[] = [
    { id: 'dashboard',    icon: <LayoutDashboard size={18} />, label: 'Dashboard',    section: 'Main' },
    { id: 'stores',       icon: <Store size={18} />,           label: 'Stores',       section: 'Main' },
    { id: 'products',     icon: <Package size={18} />,         label: 'Products',     section: 'Main' },
    { id: 'promotions',   icon: <Package size={18} />,         label: 'Promotions',   section: 'Sales' },
    { id: 'transactions', icon: <Receipt size={18} />,         label: 'Transactions', section: 'Sales' },
    { id: 'analytics',    icon: <BarChart2 size={18} />,       label: 'Analytics',    section: 'Sales' },
    { id: 'users',        icon: <Users size={18} />,           label: 'Users',        section: 'Management' },
    { id: 'settings',     icon: <Settings size={18} />,        label: 'Settings',     section: 'Management' },
    { id: 'logs', icon: <ScrollText size={18} />, label: 'Logs', section: 'Management' },
  ];

  const filtered = navItems.filter(item => allowed.includes(item.id));
  const sections = [...new Set(filtered.map(item => item.section))];

  return (
    <nav style={{
      width: '220px',
      background: '#fff',
      borderRight: '1px solid #f3f4f6',
      display: 'flex',
      flexDirection: 'column',
      maxHeight: '100vh',
      position: 'sticky',
      top: 0,
      flexShrink: 0,
      fontFamily: "'DM Sans', sans-serif",
    }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&display=swap');`}</style>

      {/* ── Logo ─────────────────────────────────────────────────────── */}
      <div style={{ padding: '24px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
        <img src={Logo} alt="Logo" style={{ width: '52px', height: '52px', borderRadius: '14px', objectFit: 'cover' }} />
        <div style={{ textAlign: 'center' }}>
          <p style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#F1561D', lineHeight: 1.2 }}>Scan Pay Go</p>
          <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#9ca3af' }}>Admin</p>
        </div>
      </div>

      {/* ── Nav ──────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 0' }}>
        {sections.map(section => (
          <div key={section} style={{ marginBottom: '4px' }}>
            <p style={{ margin: 0, padding: '8px 20px 4px', fontSize: '10px', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              {section}
            </p>
            {filtered.filter(item => item.section === section).map(item => {
              const active = currentPage === item.id;
              const hasUnread = item.id === 'users' && (totalUnread ?? 0) > 0;
              return (
                <button key={item.id} onClick={() => setCurrentPage(item.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    width: '100%', padding: '9px 20px',
                    border: 'none', textAlign: 'left', cursor: 'pointer',
                    fontSize: '14px', fontWeight: active ? 500 : 400,
                    borderRadius: 0, position: 'relative',
                    background: active ? '#fff5f2' : 'transparent',
                    color: active ? '#F1561D' : '#374151',
                    borderLeft: active ? '3px solid #F1561D' : '3px solid transparent',
                    transition: 'background 0.15s, color 0.15s',
                  }}
                  onMouseEnter={e => { if (!active) (e.currentTarget as HTMLButtonElement).style.background = '#fafafa'; }}
                  onMouseLeave={e => { if (!active) (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}>
                  <span style={{ color: active ? '#F1561D' : '#9ca3af', display: 'flex', flexShrink: 0 }}>{item.icon}</span>
                  <span style={{ flex: 1 }}>{item.label}</span>
                  {hasUnread && (
                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ef4444', flexShrink: 0 }} />
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {/* ── User pill ─────────────────────────────────────────────────── */}
      <div style={{ padding: '16px 20px', borderTop: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#fff5f2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <span style={{ fontSize: '13px', fontWeight: 600, color: '#F1561D' }}>{(user?.name || 'A')[0].toUpperCase()}</span>
        </div>
        <div style={{ minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: '13px', fontWeight: 500, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.name || 'Admin'}</p>
          <p style={{ margin: 0, fontSize: '11px', color: '#9ca3af', textTransform: 'capitalize' }}>{user?.role || 'admin'}</p>
        </div>
      </div>
    </nav>
  );
};

export default Sidebar;
